import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BiBell, BiCheckDouble, BiX } from 'react-icons/bi';
import { useNotifications } from '@/notifications/NotificationsContext';
import {
  getNotificationIcon,
  getNotificationLink,
  renderNotificationMessage,
} from '@/notifications/notificationMessages';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import { useWidgetRetryFocus } from '@/lib/useWidgetRetryFocus';
import type { NotificationItem } from '@/types/shared';
import './NotificationsPanel.css';

const MOBILE_QUERY = '(max-width: 767px)';

function useIsMobileViewport(): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(MOBILE_QUERY);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return matches;
}

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  triggerRef: RefObject<HTMLElement>;
}

const PANEL_TITLE_ID = 'notifications-panel-title';

/**
 * The bell's dropdown/panel (T115) — `16-notifications-ux.md` §2/§3/§4. Desktop: a non-modal
 * anchored popover (focus trapped, but the page behind stays pointer-operable, no overlay). Mobile
 * (`<768px`): promoted to the exact `FilterDrawer` full-screen treatment (overlay, `aria-modal`,
 * body-scroll-lock). Focus-trap/Escape/click-outside/focus-return code shape copied from
 * `ConfirmDialog.tsx`/`FilterDrawer.tsx` per the handoff note.
 */
export function NotificationsPanel({ open, onClose, triggerRef }: NotificationsPanelProps) {
  const { t, i18n } = useTranslation();
  const {
    items,
    unreadCount,
    status,
    hasMore,
    loadingMore,
    loadMoreError,
    fetchInitial,
    loadMore,
    markRead,
    markAllRead,
  } = useNotifications();

  const isMobile = useIsMobileViewport();
  const panelRef = useRef<HTMLDivElement>(null);
  const markAllButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const retriedRef = useRef(false);

  const [markAllBusy, setMarkAllBusy] = useState(false);
  const [markAllErrored, setMarkAllErrored] = useState(false);

  useWidgetRetryFocus(status, retriedRef, headingRef, retryButtonRef);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    if (isMobile && closeButtonRef.current) {
      closeButtonRef.current.focus();
    } else if (markAllButtonRef.current && !markAllButtonRef.current.disabled) {
      markAllButtonRef.current.focus();
    } else {
      panelRef.current?.focus();
    }

    let previousOverflow = '';
    if (isMobile) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onDocClick);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onDocClick);
      if (isMobile) {
        document.body.style.overflow = previousOverflow;
      }
      previouslyFocused.current?.focus();
    };
  }, [open, onClose, isMobile, triggerRef]);

  if (!open) return null;

  function handleRowActivate(item: NotificationItem) {
    onClose();
    void markRead(item.id);
  }

  async function handleMarkAllRead() {
    setMarkAllBusy(true);
    setMarkAllErrored(false);
    try {
      await markAllRead();
    } catch {
      setMarkAllErrored(true);
    } finally {
      setMarkAllBusy(false);
    }
  }

  function handleRetryInitial() {
    retriedRef.current = true;
    fetchInitial();
  }

  const isEmpty = status === 'loaded' && items.length === 0;
  const isLoadedWithItems = status === 'loaded' && items.length > 0;

  const panel = (
    <div
      className={`fa-notifications-panel${isMobile ? ' fa-notifications-panel--mobile' : ''}`}
      role="dialog"
      aria-modal={isMobile ? true : undefined}
      aria-labelledby={PANEL_TITLE_ID}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="fa-notifications-panel__header">
        <h2
          id={PANEL_TITLE_ID}
          ref={headingRef}
          tabIndex={-1}
          className="fa-notifications-panel__title"
        >
          {t('topbar.notifications')}
        </h2>
        <button
          type="button"
          ref={markAllButtonRef}
          className="fa-btn fa-btn--ghost fa-notifications-panel__mark-all"
          onClick={handleMarkAllRead}
          disabled={unreadCount === 0 || markAllBusy}
          title={unreadCount === 0 ? t('notifications.markAllReadDisabledHint') : undefined}
        >
          <BiCheckDouble aria-hidden="true" />
          {t('notifications.markAllRead')}
        </button>
        {isMobile && (
          <button
            type="button"
            ref={closeButtonRef}
            className="fa-icon-btn fa-notifications-panel__close"
            aria-label={t('notifications.panelCloseAriaLabel')}
            onClick={onClose}
          >
            <BiX aria-hidden="true" />
          </button>
        )}
      </div>

      {markAllErrored && (
        <p className="fa-notifications-panel__inline-error" role="status">
          {t('toast.genericError')}
        </p>
      )}

      <div className="fa-notifications-panel__body">
        {status === 'loading' && (
          <ul
            className="fa-notifications-panel__list"
            aria-label={t('notifications.loadingAriaLabel')}
          >
            {Array.from({ length: 6 }).map((_, idx) => (
              <li key={idx} className="fa-notifications-panel__row-skeleton" aria-hidden="true">
                <span className="fa-notifications-panel__skeleton-line fa-notifications-panel__skeleton-line--primary" />
                <span className="fa-notifications-panel__skeleton-line fa-notifications-panel__skeleton-line--secondary" />
              </li>
            ))}
          </ul>
        )}

        {status === 'error' && (
          <div className="fa-notifications-panel__error" role="status">
            <p>{t('notifications.errorMessage')}</p>
            <button
              type="button"
              ref={retryButtonRef}
              className="fa-notifications-panel__retry-btn"
              onClick={handleRetryInitial}
            >
              {t('notifications.retryLabel')}
            </button>
          </div>
        )}

        {isEmpty && (
          <div className="fa-notifications-panel__empty">
            <span className="fa-notifications-panel__empty-icon" aria-hidden="true">
              <BiBell />
            </span>
            <p className="fa-notifications-panel__empty-title">{t('notifications.emptyTitle')}</p>
            <p className="fa-notifications-panel__empty-body">{t('notifications.emptyBody')}</p>
          </div>
        )}

        {isLoadedWithItems && (
          <ul className="fa-notifications-panel__list">
            {items.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                language={i18n.language}
                onActivate={() => handleRowActivate(item)}
              />
            ))}
          </ul>
        )}
      </div>

      {isLoadedWithItems && (hasMore || loadMoreError) && (
        <div className="fa-notifications-panel__footer">
          {loadMoreError ? (
            <button type="button" className="fa-notifications-panel__retry-btn" onClick={loadMore}>
              {t('notifications.retryLabel')}
            </button>
          ) : (
            <button
              type="button"
              className="fa-notifications-panel__load-more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? t('notifications.loadingMore') : t('notifications.loadMore')}
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div className="fa-notifications-panel-overlay" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()}>{panel}</div>
      </div>
    );
  }

  return panel;
}

interface NotificationRowProps {
  item: NotificationItem;
  language: string;
  onActivate: () => void;
}

function NotificationRow({ item, language, onActivate }: NotificationRowProps) {
  const { t } = useTranslation();
  const isUnread = item.readAt === null;
  const { Icon, tone } = getNotificationIcon(item.type);
  const link = getNotificationLink(item);
  const relativeTime = formatRelativeTime(item.createdAt, language);
  const exactTime = formatDateTime(item.createdAt, language);
  const message = renderNotificationMessage(t, item);

  const content = (
    <>
      {isUnread && <span className="fa-notifications-panel__dot" aria-hidden="true" />}
      <span
        className={`fa-notifications-panel__glyph-chip fa-notifications-panel__glyph-chip--${tone}`}
        aria-hidden="true"
      >
        <Icon />
      </span>
      <span className="fa-notifications-panel__row-text">
        <span className="fa-notifications-panel__message">
          {isUnread && (
            <span className="fa-visually-hidden">{t('notifications.unreadIndicatorLabel')} </span>
          )}
          {message}
        </span>
        <span className="fa-notifications-panel__time">
          <bdi title={exactTime}>{relativeTime}</bdi>
        </span>
      </span>
    </>
  );

  const className = `fa-notifications-panel__row${isUnread ? ' fa-notifications-panel__row--unread' : ''}`;

  return (
    <li className={className}>
      {link ? (
        <Link to={link} className="fa-notifications-panel__link" onClick={onActivate}>
          {content}
        </Link>
      ) : (
        <button type="button" className="fa-notifications-panel__link" onClick={onActivate}>
          {content}
        </button>
      )}
    </li>
  );
}
